// BreakdownPage.js (UPDATED with fetchLineItemsWithHeadings)
import React, { useEffect, useState, useContext } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { fetchBreakdownByQuantityUnit, API_BASE_URL } from '../services/api';
import { useRegion } from '../context/RegionContext';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import "./PageLayout.css";
import { v4 as uuidv4 } from 'uuid';


// ✅ NEW: Fetch line items with proper auth and logging
const fetchLineItemsWithHeadings = async (quantityUnitId) => {
  try {
    console.log(`🔍 Fetching line items for material: ${quantityUnitId}`);
    
    // Get auth headers with bypass token
    const token = localStorage.getItem('authToken');
    const bypassToken = sessionStorage.getItem('rateLimitBypass');
    const region = localStorage.getItem('selectedRegion') || 'vquest';
    
    const headers = {
      'Authorization': `Bearer ${token}`,
      'region': region
    };
    
    if (bypassToken) {
      headers['X-Rate-Limit-Bypass'] = bypassToken;
      console.log('✅ Using bypass token for line items fetch');
    }

    // Use the authenticated api instance
    const response = await api.get(`/line-items/${quantityUnitId}`, { headers });
    
    console.log('📦 Line items API response:', response.data);
    
    // Handle different response structures
    const items = response.data?.data?.lineItems || response.data?.lineItems || response.data || [];
    
    // Log detailed breakdown
    console.log(`✅ Line items: ${items.length} total`);
    const withHeadings = items.filter(item => item.isHeading || item.headings);
    const withCalcRows = items.filter(item => item.line_item_rows?.length > 0);
    console.log(`   - ${withHeadings.length} items have headings`);
    console.log(`   - ${withCalcRows.length} items have calculator rows`);
    
    return items;
    
  } catch (error) {
    console.error('❌ Error fetching line items:', error);
    console.error('   Status:', error.response?.status);
    console.error('   Message:', error.response?.data?.message || error.message);
    
    // Show rate limit modal if 429
    if (error.response?.status === 429) {
      console.log('🚨 Rate limit hit - modal should show');
    }
    
    return [];
  }
};


// ✅ UPDATED: Copy from breakdown data when line items are empty
const copyBreakdownToNew = async (quantityUnitId, breakdown) => {
  try {
    console.log('📋 Starting copy breakdown to new...');
    console.log('   Material ID:', quantityUnitId);
    console.log('   Breakdown items:', breakdown.length);
    
    // Try to fetch line items first
    const lineItems = await fetchLineItemsWithHeadings(quantityUnitId);
    
    // ✅ NEW: If no line items, copy directly from breakdown data
    if (!lineItems || lineItems.length === 0) {
      console.log('📊 No line items found, copying from breakdown data...');
      
      // Group by measurement type
      const groupedData = {
        carbon: [],
        biogenic: [],
        weight: [],
        cost: []
      };
      
      const rowHistory = {};
      
      // Map measurement types to sections
      const measurementTypeMap = {
        'vquest-Co2': 'carbon',
        'CO2': 'carbon',
        'Carbon': 'carbon',
        'vquest-Co2-Biogenic': 'biogenic',
        'Biogenic': 'biogenic',
        'vquest-Weight': 'weight',
        'Weight': 'weight',
        'vquest-Rate': 'cost',
        'Cost': 'cost',
        'Rate': 'cost'
      };
      
      // Sort by sort_order if available
      const sortedBreakdown = [...breakdown].sort((a, b) => {
        const orderA = a.sort_order || 0;
        const orderB = b.sort_order || 0;
        return orderA - orderB;
      });
      
      let currentHeading = {
        carbon: null,
        biogenic: null,
        weight: null,
        cost: null
      };
      let currentSubHeading = {    // 🆕
        carbon: null, biogenic: null, weight: null, cost: null,
      };
      
      sortedBreakdown.forEach(item => {
        // Determine target section from MeasurementType
        const measurementType = item.MeasurementType || '';

        console.log('🔍 Processing item:', {
          MeasurementType: measurementType,
          CostMeasurementName: item.CostMeasurementName,
          Factor: item.Factor,
          Value: item.Value
        });

        const section = measurementTypeMap[measurementType];
        
        if (!section) {
          console.log(`⚠️ Skipping item with unknown type: ${measurementType}`);
          return;
        }
        
        // Generate new IDs
        const newLineItemId = uuidv4();
        const newId = uuidv4();
        
        // Check if this is a heading row (from breakdown data)
        if (item.heading && item.heading !== currentHeading[section]) {
          currentHeading[section] = item.heading;
          currentSubHeading[section] = null; 
          // Add heading row
          groupedData[section].push({
            id:           uuidv4(),
            lineItemId:   uuidv4(),
            measurementId:item.MeasurementType || '',
            isHeading:    true,
            isSubHeading: false,
            heading:      item.heading,
            subHeading:   '',
            description:  '', category: '', code: '',
            quantity: '', unit: '', value: '', total: '',
          });

          // ── Sub-heading 🆕 ────────────────────────────────────────────────────
        if (
          item.subHeading &&
          item.subHeading.trim() !== '' &&
          item.subHeading !== currentSubHeading[section]
        ) {
          currentSubHeading[section] = item.subHeading;
          groupedData[section].push({
            id:           uuidv4(),
            lineItemId:   uuidv4(),
            measurementId:item.MeasurementType || '',
            isHeading:    false,
            isSubHeading: true,
            heading:      currentHeading[section] || '',
            subHeading:   item.subHeading,
            description:  '', category: '', code: '',
            quantity: '', unit: '', value: '', total: '',
          });
        }
          
          // Generate new IDs for the actual item
          const itemNewLineItemId = uuidv4();
          const itemNewId = uuidv4();
          
          // Add the actual item
          groupedData[section].push({
            id: itemNewId,
            lineItemId: itemNewLineItemId,
            measurementId: item.MeasurementType || '',
            description: item.CostMeasurementName || '',
            category: item.Category || '',
            code: item.MeasurementUnitRateId || '',
            quantity: parseFloat(item.Factor || 0),
            unit: item.QuantityTypeName || '',
            value: parseFloat(item.Value || 0),
            total: parseFloat(item.TotalResult || 0),
            isHeading: false,
            isSubHeading: false,
            heading: item.heading,
            subHeading:   currentSubHeading[section] || '', 
          });
        } else {
          // No heading, just add the item
          groupedData[section].push({
            id: newId,
            lineItemId: newLineItemId,
            measurementId: item.MeasurementType || '',
            description: item.CostMeasurementName || '',
            category: item.Category || '',
            code: item.MeasurementUnitRateId || '',
            quantity: parseFloat(item.Factor || 0),
            unit: item.QuantityTypeName || '',
            value: parseFloat(item.Value || 0),
            total: parseFloat(item.TotalResult || 0),
            isHeading: false,
            isSubHeading: false,
            heading: currentHeading[section] || '',
            subHeading:   currentSubHeading[section] || '', 
          });
        }
      });
      
      // Prepare prefill data
      const prefillData = {
        carbon: groupedData.carbon,
        biogenic: groupedData.biogenic,
        weight: groupedData.weight,
        cost: groupedData.cost,
        rowHistory: rowHistory
      };
      
      console.log('✅ Prefill data prepared from breakdown:', {
        carbon: prefillData.carbon.length,
        biogenic: prefillData.biogenic.length,
        weight: prefillData.weight.length,
        cost: prefillData.cost.length
      });
      
      // Store in localStorage
      localStorage.setItem('newBreakdownPrefill', JSON.stringify(prefillData));
      console.log('💾 Breakdown data copied to localStorage');
      
      return true;
    }
    
    // ✅ Original logic when line items exist
    console.log(`📊 Processing ${lineItems.length} line items...`);

    const measurementTypeMap = {
      'Co2': 'carbon', 'co2': 'carbon',
      'Co2-Biogenic': 'biogenic', 'biogenic': 'biogenic',
      'Weight': 'weight', 'weight': 'weight',
      'Rate': 'cost', 'cost': 'cost',
    };

    const getSectionFromType = (type = '') => {
      const t = type.toLowerCase();
      if (t.includes('biogenic')) return 'biogenic';
      if (t.includes('co2'))      return 'carbon';
      if (t.includes('weight'))   return 'weight';
      if (t.includes('rate'))     return 'cost';
      return null;
    };

    const groupedData = { carbon: [], biogenic: [], weight: [], cost: [] };
    const rowHistory  = {};

    // Sort line items by sort_order
    const sortedLineItems = [...lineItems].sort((a, b) =>
      (a.sort_order ?? 9999) - (b.sort_order ?? 9999)
    );

    const currentHeading = { carbon: null, biogenic: null, weight: null, cost: null };
    const currentSubHeading = { carbon: null, biogenic: null, weight: null, cost: null }; // 🆕

    sortedLineItems.forEach((li) => {
      const section = getSectionFromType(li.measurement_type);
      if (!section) return;

      // Find matching breakdown row for display values
      const bdRow = breakdown.find(b => String(b.FactorId) === String(li.id)) || {};

      // Insert heading row if needed
      const heading = li.headings || null;
      if (heading && heading !== currentHeading[section]) {
        currentHeading[section] = heading;
        currentSubHeading[section] = null;
        groupedData[section].push({
          id: uuidv4(),
          lineItemId: uuidv4(),
          measurementId: li.measurement_type || '',
          isHeading: true,
          heading,
          subHeading:   '',
          description: '', category: '', code: '',
          quantity: '', unit: '', value: '', total: '',
        });
      }

      // ── Sub-heading 🆕 ──────────────────────────────────────────────────────
      const subHeading = li.sub_heading || null;
      if (subHeading && subHeading.trim() !== '' && subHeading !== currentSubHeading[section]) {
        currentSubHeading[section] = subHeading;
        groupedData[section].push({
          id:           uuidv4(),
          lineItemId:   uuidv4(),
          measurementId:li.measurement_type || '',
          isHeading:    false,
          isSubHeading: true,
          heading:      currentHeading[section] || '',
          subHeading,
          description:  '', category: '', code: '',
          quantity: '', unit: '', value: '', total: '',
        });
      }

      // REPLACE WITH — always mint a new ID so we don't collide with the source:
      const newLineItemId = uuidv4();  // ← never reuse source's ID

      // Store calculator rows under the NEW id
      if (li.line_item_rows) {
        try {
          const rows = typeof li.line_item_rows === 'string'
            ? JSON.parse(li.line_item_rows)
            : li.line_item_rows;
          if (Array.isArray(rows) && rows.length > 0) {
            rowHistory[newLineItemId] = rows;  // ← keyed by new ID
          }
        } catch (e) { /* ignore parse errors */ }
      }

      groupedData[section].push({
        id: uuidv4(),
        lineItemId: newLineItemId, 
        measurementId: li.measurement_type || '',
        description:   bdRow.CostMeasurementName || '',
        category:      bdRow.Category            || '',
        code:          li.measurement_unit_rate_id || bdRow.MeasurementUnitRateId || '',
        quantity:      parseFloat(li.factor ?? bdRow.Factor ?? 0),
        unit:          bdRow.QuantityTypeName     || '',
        value:         parseFloat(bdRow.Value     ?? 0),
        total:         parseFloat(bdRow.TotalResult ?? 0),
        isHeading: false,
        isSubHeading: false,
        heading:   currentHeading[section] || '',
        subHeading:   currentSubHeading[section] || '',
      });
    });

    const prefillData = {
      carbon:     groupedData.carbon,
      biogenic:   groupedData.biogenic,
      weight:     groupedData.weight,
      cost:       groupedData.cost,
      rowHistory,
    };

    console.log('✅ Prefill data prepared from line items:', {
      carbon:   prefillData.carbon.length,
      biogenic: prefillData.biogenic.length,
      weight:   prefillData.weight.length,
      cost:     prefillData.cost.length,
      rowHistoryKeys: Object.keys(rowHistory).length,
    });

    localStorage.setItem('newBreakdownPrefill', JSON.stringify(prefillData));
    console.log('💾 Line item data copied to localStorage');
    return true;
    
  } catch (error) {
    console.error('❌ Error copying breakdown:', error);
    alert('Failed to copy breakdown data. Please try again.');
    return false;
  }
};



function BreakdownPage() {
  const { quantityUnitId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const filter = location.state?.filter || null;

  const { selectedRegion } = useRegion();

  const [materialMeta, setMaterialMeta] = useState({ isPrivate: false, ownerEmail: null });

  const { user } = useContext(AuthContext);
  const role = user?.role || 'user';
  const isAdmin = role === 'admin';
  const isUser  = role === 'user';
  const roleLoading = user === undefined;

  const [breakdown, setBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleOpenInNewTab = () => {
    const currentUrl = window.location.href;
    window.open(currentUrl, '_blank', 'noopener,noreferrer');
  };

  // Helper function to format decimal places to 3
  const formatToThreeDecimals = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = parseFloat(value);
    return isNaN(num) ? value : num.toFixed(3);
  };

  useEffect(() => {
    if (!quantityUnitId || !selectedRegion) return;
    setLoading(true);
    
    // Fetch breakdown data
    fetchBreakdownByQuantityUnit(quantityUnitId, filter)
      .then(async (res) => {
        console.log('🔍 Breakdown API response:', res);
        const responseData = res.data?.data || res.data || res || [];
        const finalData = Array.isArray(responseData) ? responseData : [];
        console.log('✅ Setting breakdown data:', finalData.length, 'items');

        if (res.data?.meta) {
          setMaterialMeta(res.data.meta);
        }
        
        // ✅ UPDATED: Use the new fetch function
        try {
          const lineItems = await fetchLineItemsWithHeadings(quantityUnitId);
          
          if (lineItems.length > 0) {
            console.log('📊 Sample line item:', lineItems[0]);
            
            // ✅ Enrich breakdown data with headings
            const enrichedData = finalData.map(item => {
              const lineItem = lineItems.find(li => String(li.id) === String(item.FactorId));
              
              return {
                ...item,
                heading: lineItem?.headings || lineItem?.heading || null,
                subHeading: lineItem?.sub_heading || lineItem?.subHeading || null,
                sort_order: lineItem?.sort_order !== undefined ? lineItem.sort_order : 9999
              };
            });
            
            // ✅ Sort by sort_order
            enrichedData.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
            
            console.log('✅ Enriched data with headings:', enrichedData.filter(i => i.heading).length, 'items have headings');
            
            setBreakdown(enrichedData);
          } else {
            console.log('⚠️ No line items returned, using breakdown data without headings');
            setBreakdown(finalData);
          }
        } catch (error) {
          console.error('❌ Error in line items fetch/enrichment:', error);
          console.log('⚠️ Falling back to breakdown data without headings');
          setBreakdown(finalData);
        }
      })
      .catch((error) => {
        console.error('❌ Error fetching breakdown:', error);
        setBreakdown([]);
      })
      .finally(() => setLoading(false));
  }, [quantityUnitId, filter, selectedRegion]);

  const groupedBreakdown = breakdown.reduce((acc, item) => {
    const type = item.MeasurementType || 'Unknown';

    // Hide Rate sections from non-admin users
    const isRateSection = type.toLowerCase().includes('rate') || type.toLowerCase().includes('cost');
    if (isRateSection && !isAdmin) {
      return acc;
    }
    (acc[type] ||= []).push(item);
    return acc;
  }, {});

  // Handle back navigation
  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div>
        <div className="page-toolbar">
          <button className="back-btn" onClick={handleBack}>← Back</button>
          <div />
          <div className="page-actions" />
        </div>
        <h1>Breakdown for {isAdmin ? `QuantityUnitID: ${quantityUnitId}` : (breakdown?.[0]?.QuantityUnitName || quantityUnitId)}</h1>
        <p>Loading breakdown data for {selectedRegion}...</p>
      </div>
    );
  }

  return (
    <div>
      {/* TOP BAR */}
      <div className="page-toolbar">
        <button className="back-btn" onClick={handleBack}>← Back</button>

        <h1 className="page-title">Breakdown for {isAdmin ? `QuantityUnitID: ${quantityUnitId}` : (breakdown?.[0]?.QuantityUnitName || quantityUnitId)}</h1>

        <div className="page-actions">
          <button
            onClick={handleOpenInNewTab}
            className="open-new-tab-button"
            title="Open in New Tab"
            style={{
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginRight: '10px'
            }}
          >
            {/* New Tab Icon (SVG) */}
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
              <polyline points="15 3 21 3 21 9"></polyline>
              <line x1="10" y1="14" x2="21" y2="3"></line>
            </svg>
            Open in New Tab
          </button>
          
          {isAdmin && !roleLoading && (
            <>
              <button
                onClick={() => navigate('/materialeditor', { state: { materialId: quantityUnitId } })}
                className="edit-material-button edit-material-button--admin"
                style={{ marginRight: '10px' }}
              >
                Edit Material
              </button>
              
              <button
                onClick={async () => {
                  const success = await copyBreakdownToNew(quantityUnitId, breakdown);
                  if (success) {
                    navigate('/newbreakdown');
                  }
                }}
                className="edit-material-button edit-material-button--admin"
                style={{
                  backgroundColor: '#7FCAB1',
                  marginRight: '10px'
                }}
              >
                Copy Breakdown
              </button>
            </>
          )}


          {isUser && !roleLoading && (
            <button
              onClick={() =>
                navigate('/composite-requests', {
                  state: { prefill: { materialId: quantityUnitId, materialName: breakdown?.[0]?.QuantityUnitName || null } }
                })
              }
              className="edit-material-button edit-material-button--user"
            >
              Create Copy
            </button>
          )}
        </div>
      </div>

      <div className="region-indicator">
        🌏 Current Region: <strong>{selectedRegion}</strong>
        <span className="role-indicator" style={{ marginLeft: 20 }}>
          👤 Role: <strong>{roleLoading ? '...' : role.toUpperCase()}</strong>
        </span>
      </div>
      {/* ← ADD THIS */}
      {materialMeta.isPrivate && (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          margin: '8px 0 16px',
          padding: '6px 14px',
          borderRadius: 8,
          background: '#fef3c7',
          border: '1px solid #f59e0b',
          color: '#92400e',
          fontWeight: 'bold',
          fontSize: 13,
        }}>
          🔒 Private material
          <span style={{ fontWeight: 'normal' }}>
            — only visible to {materialMeta.ownerEmail}
          </span>
        </div>
      )}

      {breakdown.length === 0 ? (
        <p>No breakdown data found for {quantityUnitId} in region {selectedRegion}</p>
      ) : (
        <div>
          {Object.keys(groupedBreakdown)
            .filter(measurementType => {
              const isRateSection = measurementType.toLowerCase().includes('rate') || measurementType.toLowerCase().includes('cost');
              return isAdmin || !isRateSection;
            })
            .map((measurementType) => {
              // ✅ Insert heading rows into the data
              const items = groupedBreakdown[measurementType];
              const itemsWithHeadings = [];
              let currentHeading = null;
              let currentSubHeading = null;

              items.forEach(item => {
                // If this item has a heading different from current, insert heading row
                if (item.heading && item.heading !== currentHeading) {
                  currentHeading = item.heading;
                  currentSubHeading = null; 
                  itemsWithHeadings.push({
                    isHeading: true,
                    isSubHeading: false,
                    heading: currentHeading,
                    FactorId: `heading-${currentHeading}-${Math.random()}`
                  });
                }

                // ── Sub-heading 🆕 ───────────────────────────────────────────
                if (
                  item.subHeading &&
                  item.subHeading.trim() !== '' &&
                  item.subHeading !== currentSubHeading
                ) {
                  currentSubHeading = item.subHeading;
                  itemsWithHeadings.push({
                    isHeading:    false,
                    isSubHeading: true,
                    subHeading:   currentSubHeading,
                    FactorId:     `subheading-${currentSubHeading}-${Math.random()}`,
                  });
                }

                itemsWithHeadings.push(item);
              });

              return (
                <div key={measurementType} className="breakdown-section">
                  <h3>{measurementType} Breakdown</h3>
                  <table>
                    <thead>
                      <tr>
                        <th>Id</th>
                        <th>Name</th>
                        <th>Product</th>
                        <th>Unit</th>
                        <th>Factor</th>
                        <th>Value</th>
                        <th>Total</th>
                        <th>Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemsWithHeadings.map((item, idx) => {
                        // ── Heading row ───────────────────────────────────────
                        if (item.isHeading) {
                          return (
                            <tr key={item.FactorId} className="heading-row">
                              <td colSpan="8" style={{
                                backgroundColor: '#f0f4f8',
                                fontWeight:      'bold',
                                padding:         '12px',
                                borderLeft:      '4px solid #4a90e2',
                                fontSize:        '14px',
                              }}>
                                {item.heading}
                              </td>
                            </tr>
                          );
                        }
 
                        // ── Sub-heading row 🆕 ────────────────────────────────
                        if (item.isSubHeading) {
                          return (
                            <tr key={item.FactorId} className="subheading-row">
                              <td colSpan="8" style={{
                                backgroundColor: '#f7f9fc',
                                fontWeight:      '600',
                                padding:         '8px 12px 8px 28px',
                                borderLeft:      '3px solid #a0bfdf',
                                fontSize:        '13px',
                                color:           '#2c5282',
                              }}>
                                └ {item.subHeading}
                              </td>
                            </tr>
                          );
                        }
 
                        // ── Regular data row ──────────────────────────────────
                        return (
                          <tr key={`${item.FactorId}-${idx}`}>
                            <td>{item.FactorId}</td>
                            <td>{item.QuantityUnitName}</td>
                            <td>{item.CostMeasurementName}</td>
                            <td>{item.QuantityTypeName}</td>
                            <td>{formatToThreeDecimals(item.Factor)}</td>
                            <td>{formatToThreeDecimals(item.Value)}</td>
                            <td>{formatToThreeDecimals(item.TotalResult)}</td>
                            <td>
                              {item.Ref_link
                                ? <a href={item.Ref_link} target="_blank" rel="noopener noreferrer">View source</a>
                                : ''}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}

export default BreakdownPage;
